import { useState } from "react";
import "./map.css";

// Relationship type labels
const RELATIONSHIP_LABELS = {
  SIMILAR_TECHNOLOGY: "Similar Tech",
  SIMILAR_APPLICATION: "Similar Application",
  SEMANTICALLY_SIMILAR: "Semantic Similarity",
  HIGHLY_SIMILAR: "Highly Similar",
  SHARES_TECHNOLOGY: "Shares Technology",
  SHARES_APPLICATION: "Shares Application",
  SAME_LIFECYCLE_STAGE: "Same Lifecycle",
  SAME_TARGET_MARKET: "Same Market",
  SAME_TRL_RANGE: "Similar TRL",
  SAME_FUNDING_STAGE: "Same Funding Stage",
  COMPLEMENTARY_TECH: "Complementary Tech",
  COLLABORATES_WITH: "Collaborates",
  SAME_TYPE: "Same Type",
};

// Relationship type descriptions
const RELATIONSHIP_INFO = {
  SIMILAR_TECHNOLOGY: {
    label: "Similar Tech",
    desc: "Companies using similar or related technologies based on AI semantic analysis (similarity ≥75%)",
  },
  SIMILAR_APPLICATION: {
    label: "Similar Application",
    desc: "Companies targeting similar fire/wildfire application areas based on AI semantic analysis (similarity ≥75%)",
  },
  SEMANTICALLY_SIMILAR: {
    label: "Semantic Similarity",
    desc: "Companies with similar overall descriptions, goals, or missions based on AI analysis (similarity ≥70%)",
  },
  HIGHLY_SIMILAR: {
    label: "Highly Similar",
    desc: "Companies highly similar across multiple dimensions - likely direct competitors (similarity ≥80%)",
  },
  SHARES_TECHNOLOGY: {
    label: "Shares Technology",
    desc: "Companies using at least one identical technology from their technology lists",
  },
  SHARES_APPLICATION: {
    label: "Shares Application",
    desc: "Companies targeting at least one identical fire application area",
  },
  SAME_LIFECYCLE_STAGE: {
    label: "Same Lifecycle",
    desc: "Companies focusing on the same part of the wildfire disaster lifecycle (Prevention, Response, Recovery)",
  },
  SAME_TARGET_MARKET: {
    label: "Same Market",
    desc: "Companies serving the same target markets or customer segments",
  },
  SAME_TRL_RANGE: {
    label: "Similar TRL",
    desc: "Companies at similar Technology Readiness Levels (within 2 levels) - similar development stages",
  },
  SAME_FUNDING_STAGE: {
    label: "Same Funding Stage",
    desc: "Companies at the same stage of funding (Seed, Series A, Revenue Generating, etc.)",
  },
  COMPLEMENTARY_TECH: {
    label: "Complementary Tech",
    desc: "Companies with technologies that complement each other (e.g., Detection complements Response)",
  },
  COLLABORATES_WITH: {
    label: "Collaborates",
    desc: "Companies within 500 miles of each other - potential collaboration based on proximity",
  },
  SAME_TYPE: {
    label: "Same Type",
    desc: "Companies with the same organizational type or classification",
  },
};

const RELATIONSHIP_COLORS = {
  COLLABORATES_WITH: "#6bff6b",
  SAME_TYPE: "#ffaa6b",
  SIMILAR_TECHNOLOGY: "#4a9eff",
  SIMILAR_APPLICATION: "#9b59b6",
  SEMANTICALLY_SIMILAR: "#e67e22",
  HIGHLY_SIMILAR: "#e74c3c",
  SHARES_TECHNOLOGY: "#3498db",
  SHARES_APPLICATION: "#8e44ad",
  SAME_LIFECYCLE_STAGE: "#f39c12",
  SAME_TARGET_MARKET: "#16a085",
  SAME_TRL_RANGE: "#27ae60",
  SAME_FUNDING_STAGE: "#c0392b",
  COMPLEMENTARY_TECH: "#d35400",
};

function getRelationshipColor(relType) {
  return RELATIONSHIP_COLORS[relType] || "#ffffff";
}

const MapControls = ({ relationshipTypes = [], onFilterChange, selectedFilter }) => {
  const [legendOpen, setLegendOpen] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState({});

  const handleFilterChange = (e) => {
    const value = e.target.value;
    if (onFilterChange) {
      onFilterChange(value);
    }
  };

  const toggleLegend = () => {
    setLegendOpen(!legendOpen);
  };

  const toggleTypeExpansion = (type) => {
    setExpandedTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Filter and sort relationship types
  const sortedTypes = relationshipTypes
    .filter((typeData) => typeData.count > 0)
    .sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <>
      {/* Top overlay with title and filter */}
      <div className="map-overlay top">
        <div className="map-overlay-inner">
          <h1>CWPC Innovator Map</h1>
          <p>Discover innovative companies working on climate and wildfire solutions</p>
          <div className="filter-controls">
            <label htmlFor="relationship-filter">Filter by Relationship Type:</label>
            <select
              id="relationship-filter"
              value={selectedFilter || "all"}
              onChange={handleFilterChange}
            >
              <option value="all">All Relationships</option>
              {sortedTypes.map((typeData) => {
                const label = RELATIONSHIP_LABELS[typeData.type] || typeData.type;
                return (
                  <option key={typeData.type} value={typeData.type}>
                    {label} ({typeData.count || 0})
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Bottom-right overlay with legend */}
      <div className="map-overlay bottom-right" id="legend-overlay">
        <div className="map-overlay-inner">
          <div className="legend-header">
            <h3>Legend</h3>
            <button className="legend-toggle" onClick={toggleLegend}>
              {legendOpen ? "−" : "+"}
            </button>
          </div>
          {legendOpen && (
            <div className="legend-content" id="legend-content">
              <div className="legend-section">
                <h4>Companies</h4>
                <div className="legend-item">
                  <span className="legend-dot"></span>
                  <span>Innovator Company</span>
                </div>
              </div>
              <div className="legend-section">
                <h4>Relationship Types</h4>
                <div id="relationship-legend">
                  {sortedTypes.map((typeData) => {
                    const color = getRelationshipColor(typeData.type);
                    const info = RELATIONSHIP_INFO[typeData.type] || {
                      label: typeData.type,
                      desc: "Relationship between companies",
                    };
                    const isExpanded = expandedTypes[typeData.type];

                    return (
                      <div key={typeData.type} className="legend-item expandable">
                        <div
                          className="legend-item-header"
                          onClick={() => toggleTypeExpansion(typeData.type)}
                        >
                          <span className="legend-line" style={{ backgroundColor: color }}></span>
                          <span className="legend-label">{info.label}</span>
                          <span className="legend-count">({typeData.count || 0})</span>
                          <span className="legend-expand">{isExpanded ? "−" : "+"}</span>
                        </div>
                        {isExpanded && (
                          <div className="legend-description">{info.desc}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="legend-section">
                <h4>Strength</h4>
                <div className="legend-strength">
                  <div className="strength-item">
                    <span className="strength-line strong"></span>
                    <span>Strong (≥85%)</span>
                  </div>
                  <div className="strength-item">
                    <span className="strength-line medium"></span>
                    <span>Medium (70-85%)</span>
                  </div>
                  <div className="strength-item">
                    <span className="strength-line weak"></span>
                    <span>Weak (60-70%)</span>
                  </div>
                </div>
              </div>
              <p className="legend-note">Click on any dot or line to learn more</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MapControls;

